import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { SeoMetaTag } from './seo-meta-tag.service';

@Injectable({
  providedIn: 'root'
})
export class MetaTagService {

  constructor(
    private readonly titleService: Title,
    private readonly metaService: Meta
  ) {}

  updateMetaTags(seoData: SeoMetaTag): void {
    this.titleService.setTitle(seoData.metaTitle);
    this.metaService.updateTag({ name: 'description', content: seoData.metaDescription });

    if (seoData.metaKeywords) {
      this.metaService.updateTag({ name: 'keywords', content: seoData.metaKeywords });
    }

    const ogTitle = seoData.ogTitle || seoData.metaTitle;
    const ogDescription = seoData.ogDescription || seoData.metaDescription;

    this.metaService.updateTag({ property: 'og:title', content: ogTitle });
    this.metaService.updateTag({ property: 'og:description', content: ogDescription });

    if (seoData.ogImage) {
      this.metaService.updateTag({ property: 'og:image', content: seoData.ogImage });
    }

    const twitterTitle = seoData.twitterTitle || seoData.metaTitle;
    const twitterDescription = seoData.twitterDescription || seoData.metaDescription;

    this.metaService.updateTag({ name: 'twitter:title', content: twitterTitle });
    this.metaService.updateTag({ name: 'twitter:description', content: twitterDescription });

    if (seoData.twitterImage) {
      this.metaService.updateTag({ name: 'twitter:image', content: seoData.twitterImage });
    }

    if (seoData.canonicalUrl) {
      this.updateCanonicalUrl(seoData.canonicalUrl);
    }

    this.updateRobotsTag(seoData.noIndex, seoData.noFollow);
  }

  updateTitle(title: string): void {
    this.titleService.setTitle(title);
  }

  updateDescription(description: string): void {
    this.metaService.updateTag({ name: 'description', content: description });
  }

  updateCanonicalUrl(url: string): void {
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }

    const link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    document.head.appendChild(link);
  }

  updateRobotsTag(noIndex: boolean, noFollow: boolean): void {
    const robotsDirectives: string[] = [];
    robotsDirectives.push(noIndex ? 'noindex' : 'index');
    robotsDirectives.push(noFollow ? 'nofollow' : 'follow');
    this.metaService.updateTag({ name: 'robots', content: robotsDirectives.join(', ') });
  }

  setDefaultMetaTags(): void {
    this.titleService.setTitle('RMA | Property Management Software');
    this.metaService.updateTag({
      name: 'description',
      content: 'Centralize leasing, maintenance, payments, and tenant communication with RMA property management software.'
    });
    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
  }

  updateDynamicMetaTags(title: string, description: string, imageUrl?: string, url?: string): void {
    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'description', content: description });

    this.metaService.updateTag({ property: 'og:title', content: title });
    this.metaService.updateTag({ property: 'og:description', content: description });

    if (imageUrl) {
      this.metaService.updateTag({ property: 'og:image', content: imageUrl });
      this.metaService.updateTag({ name: 'twitter:image', content: imageUrl });
    }

    if (url) {
      this.metaService.updateTag({ property: 'og:url', content: url });
      this.updateCanonicalUrl(url);
    }

    this.metaService.updateTag({ name: 'twitter:title', content: title });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
  }
}
